import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  Box,
  Typography,
  Stack,
  SvgIcon
} from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  ChevronRight,
  FileText,
  CheckCircle2,
  Clock,
  FileEdit,
  Trash2
} from "lucide-react";

// Styled components for custom styling
const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: "18px",
  border: "1px solid #e2e8f0",
  boxShadow: "0 8px 30px rgba(0,0,0,0.02)",
  overflow: "hidden",
  width: "100%",
  backgroundColor: "#ffffff"
}));

const StyledTableHead = styled(TableHead)(({ theme }) => ({
  backgroundColor: "#f8fafc",
  "& .MuiTableCell-head": {
    padding: "16px 24px",
    fontSize: "10px",
    fontWeight: 700,
    color: "#94a3b8",
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    borderBottom: "1px solid #f1f5f9",
    whiteSpace: "nowrap"
  }
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  padding: "20px 24px",
  borderBottom: "1px solid #f1f5f9",
  verticalAlign: "middle"
}));

const StyledTableRow = styled(TableRow)(({ theme, isDeleted }) => ({
  transition: "all 0.3s ease",
  "&:hover": {
    backgroundColor: "#f8fafc",
    transform: isDeleted ? "none" : "translateY(-2px)",
    boxShadow: isDeleted ? "none" : "0 8px 30px rgba(15,23,42,0.02)"
  },
  ...(isDeleted && {
    backgroundColor: "#f8fafc",
    opacity: 0.8,
    filter: "grayscale(10%)"
  })
}));

const StatusChip = styled(Chip)(({ theme, status, isDeleted }) => {
  const getStatusStyles = () => {
    const normalizedStatus = (status || "UNKNOWN").toUpperCase();
    
    if (isDeleted) {
      return {
        backgroundColor: "#e2e8f0",
        color: "#64748b",
        borderColor: "#cbd5e1"
      };
    }
    
    switch (normalizedStatus) {
      case 'SENT':
      case 'SIGNED':
        return {
          backgroundColor: "#ecfdf5",
          color: "#047857",
          borderColor: "#bbf7d0"
        };
      case 'DRAFT':
        return {
          backgroundColor: "#fffbeb",
          color: "#b45309",
          borderColor: "#fde68a"
        };
      case 'PENDING':
      case 'PENDING_SIGNATURE':
        return {
          backgroundColor: "#eff6ff",
          color: "#1d4ed8",
          borderColor: "#bfdbfe"
        };
      default:
        return {
          backgroundColor: "#f8fafc",
          color: "#475569",
          borderColor: "#e2e8f0"
        };
    }
  };

  const styles = getStatusStyles();

  return {
    borderRadius: "9999px",
    fontSize: "10px",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    height: "24px",
    backgroundColor: styles.backgroundColor,
    color: styles.color,
    border: `1px solid ${styles.borderColor}`,
    "& .MuiChip-icon": {
      marginLeft: "6px",
      marginRight: "4px",
      color: isDeleted ? "#64748b" : undefined
    }
  };
});

const DeletedChip = styled(Chip)(({ theme }) => ({
  borderRadius: "6px",
  fontSize: "9px",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  height: "20px",
  backgroundColor: "#fef2f2",
  color: "#ef4444",
  border: "1px solid #fecaca",
  boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
  "& .MuiChip-icon": {
    marginLeft: "4px",
    marginRight: "2px",
    fontSize: "10px"
  }
}));

const ActionButton = styled(Button)(({ theme, isDeleted }) => ({
  borderRadius: "12px",
  fontSize: "12px",
  fontWeight: 600,
  textTransform: "none",
  padding: "6px 12px",
  minWidth: "auto",
  gap: "4px",
  transition: "all 0.2s ease",
  ...(isDeleted
    ? {
        backgroundColor: "#f8fafc",
        color: "#64748b",
        border: "1px solid #f1f5f9",
        "&:hover": {
          backgroundColor: "#f1f5f9",
          color: "#475569"
        }
      }
    : {
        backgroundColor: "rgba(59, 130, 246, 0.05)",
        color: "#2563eb",
        border: "1px solid rgba(59, 130, 246, 0.15)",
        "&:hover": {
          backgroundColor: "rgba(59, 130, 246, 0.15)",
          color: "#1d4ed8"
        }
      }),
  "& .MuiButton-endIcon": {
    marginLeft: "2px",
    "& svg": {
      transition: "transform 0.2s ease"
    }
  },
  "&:hover .MuiButton-endIcon svg": {
    transform: "translateX(2px)"
  }
}));

const EmptyStateBox = styled(Box)(({ theme }) => ({
  backgroundColor: "#ffffff",
  borderRadius: "12px",
  border: "1px solid #e2e8f0",
  padding: "64px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  boxShadow: "0 1px 2px rgba(0,0,0,0.05)"
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  height: "64px",
  width: "64px",
  backgroundColor: "#f8fafc",
  borderRadius: "16px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  marginBottom: "16px",
  border: "1px solid #f1f5f9"
}));

// Helper function to get status icon
const getStatusIcon = (status, isDeleted) => {
  const normalizedStatus = (status || "UNKNOWN").toUpperCase();

  if (isDeleted) {
    return Trash2;
  }

  switch (normalizedStatus) {
    case 'SENT':
    case 'SIGNED':
      return CheckCircle2;
    case 'DRAFT':
      return FileEdit;
    case 'PENDING':
    case 'PENDING_SIGNATURE':
      return Clock;
    default:
      return FileText;
  }
};

export const AuditLogTable = ({ logs, onView, currentUser }) => {
  if (!logs || logs.length === 0) {
    return (
      <EmptyStateBox>
        <IconWrapper>
          <SvgIcon component={FileText} sx={{ color: "#94a3b8", fontSize: 32 }} />
        </IconWrapper>
        <Typography variant="h6" sx={{ fontWeight: 600, color: "#1e293b", mb: 0.5 }}>
          No Records Found
        </Typography>
        <Typography variant="body2" sx={{ color: "#64748b" }}>
          There are no issued letters in the audit log yet.
        </Typography>
      </EmptyStateBox>
    );
  }

  return (
    <StyledTableContainer component={Paper}>
      <Table sx={{ minWidth: 650 }}>
        <StyledTableHead>
          <TableRow>
            <TableCell>Date & Time</TableCell>
            <TableCell>Last Updated</TableCell>
            <TableCell>Sent By</TableCell>
            <TableCell>Recipient</TableCell>
            <TableCell>Subject</TableCell>
            <TableCell>Status</TableCell>
            <TableCell align="right">Action</TableCell>
          </TableRow>
        </StyledTableHead>
        <TableBody>
          {logs.map((log) => {
            const recipient = log.letter_recipients?.[0] || log.LetterRecipients?.[0] || {};

            const createdDate = new Date(log.created_at || log.createdAt || log.created_on);
            const updatedDate = new Date(log.updated_at || log.updatedAt);

            // We consider it edited if they are different by more than 10 seconds
            const hasBeenEdited = Math.abs(updatedDate.getTime() - createdDate.getTime()) > 10000;

            const formattedCreatedDate = createdDate.toLocaleDateString('en-GB', {
              day: '2-digit', month: 'short', year: 'numeric'
            });
            const formattedCreatedTime = createdDate.toLocaleTimeString('en-US', {
              hour: '2-digit', minute: '2-digit'
            });

            const formattedUpdatedDate = updatedDate.toLocaleDateString('en-GB', {
              day: '2-digit', month: 'short', year: 'numeric'
            });
            const formattedUpdatedTime = updatedDate.toLocaleTimeString('en-US', {
              hour: '2-digit', minute: '2-digit'
            });

            // Check if the record is deleted
            const isDeleted = log.is_active === 0 || log.is_active === false;
            const StatusIcon = getStatusIcon(log.status, isDeleted);

            // Get entity type
            const entityType = log.LetterLink?.entity_type || (log.company_id ? "company" : "System");

            return (
              <StyledTableRow key={log.letter_id} isDeleted={isDeleted}>
                {/* Date & Time */}
                <StyledTableCell sx={{ whiteSpace: "nowrap" }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: "#475569", lineHeight: 1.2 }}>
                    {formattedCreatedDate}
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: 500, color: "#94a3b8", display: "block", mt: 0.5 }}>
                    {formattedCreatedTime}
                  </Typography>
                </StyledTableCell>

                {/* Last Updated */}
                <StyledTableCell sx={{ whiteSpace: "nowrap" }}>
                  {hasBeenEdited ? (
                    <>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: "#059669", lineHeight: 1.2 }}>
                        {formattedUpdatedDate}
                      </Typography>
                      <Typography variant="caption" sx={{ fontWeight: 500, color: "#059669", display: "block", mt: 0.5 }}>
                        {formattedUpdatedTime}
                      </Typography>
                    </>
                  ) : (
                    <Typography variant="caption" sx={{ color: "#cbd5e1", fontWeight: 600, userSelect: "none" }}>
                      —
                    </Typography>
                  )}
                </StyledTableCell>

                {/* Sent By */}
                <StyledTableCell sx={{ whiteSpace: "nowrap" }}>
                  <Stack spacing={0.5}>
                    <Chip
                      label={entityType.charAt(0).toUpperCase() + entityType.slice(1)}
                      size="small"
                      sx={{
                        backgroundColor: "#f8fafc",
                        border: "1px solid #f1f5f9",
                        fontSize: "10px",
                        fontWeight: 700,
                        color: "#475569",
                        height: "22px",
                        borderRadius: "8px",
                        alignSelf: "flex-start"
                      }}
                    />
                    {log.LetterLink?.creator_name && (
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 600,
                          color: "#64748b",
                          maxWidth: "150px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          lineHeight: 1.2,
                          pl: 0.5
                        }}
                        title={log.LetterLink.creator_name}
                      >
                        {log.LetterLink.creator_name}
                      </Typography>
                    )}
                  </Stack>
                </StyledTableCell>

                {/* Recipient */}
                <StyledTableCell sx={{ minWidth: "200px" }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: "#334155", lineHeight: 1.2 }}>
                    {recipient.name || "Unknown"}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 600,
                      color: "#94a3b8",
                      display: "block",
                      mt: 0.5,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap"
                    }}
                  >
                    {recipient.email}
                  </Typography>
                </StyledTableCell>

                {/* Subject */}
                <StyledTableCell sx={{ minWidth: "200px" }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: "#334155",
                      lineHeight: 1.2,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap"
                    }}
                  >
                    {log.subject || "Unknown"}
                  </Typography>
                </StyledTableCell>

                {/* Status */}
                <StyledTableCell sx={{ whiteSpace: "nowrap" }}>
                  <Stack spacing={0.75}>
                    <StatusChip
                      status={log.status}
                      isDeleted={isDeleted}
                      icon={<SvgIcon component={StatusIcon} sx={{ fontSize: 12 }} />}
                      label={(log.status || "UNKNOWN").replace('_', ' ')}
                      variant="outlined"
                    />

                    {isDeleted && (
                      <DeletedChip
                        icon={<SvgIcon component={Trash2} sx={{ fontSize: 10 }} />}
                        label="Deleted"
                        variant="outlined"
                      />
                    )}
                  </Stack>
                </StyledTableCell>

                {/* Action */}
                <StyledTableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                  <ActionButton
                    isDeleted={isDeleted}
                    onClick={() => onView(log)}
                    endIcon={<SvgIcon component={ChevronRight} sx={{ fontSize: 14 }} />}
                  >
                    View Details
                  </ActionButton>
                </StyledTableCell>
              </StyledTableRow>
            );
          })}
        </TableBody>
      </Table>
    </StyledTableContainer>
  );
};